import type { FC } from 'react';
import { useMemo } from 'react';

import { Space, Spacer, Text } from '@procurify/ui'

import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import {
  type IFixedBudgetSubtotal,
  type IFlexibleBudgetSubtotal,
} from '@webapp/hooks/useFormatBudget/types'
import { BudgetCategoryCardSubtotalsStyled } from './styles/BudgetCategoryCardSubtotalsStyled'
import messages from '../../messages'
import { BudgetCategoryCardSubtotal } from '../BudgetCategoryCardSubtotal'
import { BudgetCategoryCardSubtotalList } from '../BudgetCategoryCardSubtotalList'

type TBudgetSubtotal = IFixedBudgetSubtotal | IFlexibleBudgetSubtotal

interface IBudgetCategoryCardDetailsBreakdownProps {
  isOverBudget: boolean
  committedSpend: TBudgetSubtotal
  approvedSpend: TBudgetSubtotal
  billedSpend: TBudgetSubtotal
  purchasedSpend: TBudgetSubtotal
  receivedSpend: TBudgetSubtotal
  pendingRequests: TBudgetSubtotal
  totalSpend: TBudgetSubtotal
  spendRequest?: TBudgetSubtotal
  totalBudget?: TBudgetSubtotal
  remaining?: TBudgetSubtotal
  remainingAfterPendingRequests?: TBudgetSubtotal
  shouldRenderPercentagesFirst?: boolean
  shouldRenderOverageTagsBelowText?: boolean
  shouldRenderAccordion?: boolean
}

export const BudgetCategoryCardSubtotals: FC<
  IBudgetCategoryCardDetailsBreakdownProps
> = ({
  isOverBudget,
  totalBudget,
  totalSpend,
  committedSpend,
  approvedSpend,
  billedSpend,
  purchasedSpend,
  receivedSpend,
  spendRequest,
  remaining,
  pendingRequests,
  remainingAfterPendingRequests,
  shouldRenderPercentagesFirst = false,
  shouldRenderOverageTagsBelowText = false,
  shouldRenderAccordion = false,
}) => {
  const FT_5815_ENABLE_RECEIVED_CATEGORY = useFeatureFlag(
    'FT_5815_ENABLE_RECEIVED_CATEGORY'
  )
  const intl = useProcurifyIntl()

  const renderCommittedTooltip = useMemo(() => {
    return (
      <Space>
        <Space.Item>
          <Text align='left'>
            {intl.formatMessage(messages.subtotalsCommittedTooltipLine1)}
          </Text>
        </Space.Item>
        <Space.Item>
          <Text align='left'>
            {intl.formatMessage(messages.subtotalsCommittedTooltipLine2)}
          </Text>
        </Space.Item>
      </Space>
    )
  }, [])

  const renderCommittedSubtotals = useMemo(() => {
    if (shouldRenderAccordion) {
      const subtotalList = [
        {
          label: intl.formatMessage(messages.subtotalsApprovedLabel),
          amount: approvedSpend.amount,
        },
        {
          label: intl.formatMessage(messages.subtotalsPurchasedLabel),
          amount: purchasedSpend.amount,
        },
        {
          label: intl.formatMessage(messages.subtotalsBilledLabel),
          amount: billedSpend.amount,
        },
      ]
      // conditionally add received spend after purchased spend
      if (FT_5815_ENABLE_RECEIVED_CATEGORY) {
        subtotalList.splice(2, 0, {
          label: intl.formatMessage(messages.subtotalsReceivedLabel),
          amount: receivedSpend.amount,
        })
      }

      return (
        <BudgetCategoryCardSubtotalList
          label={intl.formatMessage(messages.subtotalsCommittedLabel)}
          labelTooltip={renderCommittedTooltip}
          amount={committedSpend.amount}
          percentage={committedSpend.percentage?.amount}
          percentageOverBudget={committedSpend?.percentage?.overage}
          indicatorColor={isOverBudget ? 'red' : 'blue'}
          shouldRenderPercentageFirst={shouldRenderPercentagesFirst}
          shouldRenderOverageTagBelowText={shouldRenderOverageTagsBelowText}
          subtotalList={subtotalList}
        />
      )
    }

    return (
      <BudgetCategoryCardSubtotal
        label={intl.formatMessage(messages.subtotalsCommittedLabel)}
        labelTooltip={renderCommittedTooltip}
        amount={committedSpend.amount}
        percentage={committedSpend?.percentage?.amount}
        percentageOverBudget={committedSpend?.percentage?.overage}
        indicatorColor={isOverBudget ? 'red' : 'blue'}
        shouldRenderPercentageFirst={shouldRenderPercentagesFirst}
        shouldRenderOverageTagBelowText={shouldRenderOverageTagsBelowText}
      />
    )
  }, [
    shouldRenderAccordion,
    committedSpend,
    approvedSpend,
    purchasedSpend,
    billedSpend,
    receivedSpend,
  ])

  return (
    <BudgetCategoryCardSubtotalsStyled>
      <Space size='xsmall'>
        {totalBudget && (
          <BudgetCategoryCardSubtotal
            label={intl.formatMessage(messages.subtotalsTotalLabel)}
            amount={totalBudget.amount}
            percentage={totalBudget?.percentage?.amount}
            percentageOverBudget={totalBudget?.percentage?.overage}
            shouldRenderPercentageFirst={shouldRenderPercentagesFirst}
            shouldRenderOverageTagBelowText={shouldRenderOverageTagsBelowText}
          />
        )}

        {renderCommittedSubtotals}

        {spendRequest && (
          <BudgetCategoryCardSubtotal
            label={intl.formatMessage(messages.thisRequestLabel)}
            amount={spendRequest.amount}
            percentage={spendRequest?.percentage?.amount}
            percentageOverBudget={spendRequest?.percentage?.overage}
            indicatorColor={isOverBudget ? 'red' : 'blue'}
            indicatorStriped
            shouldRenderPercentageFirst={shouldRenderPercentagesFirst}
            shouldRenderOverageTagBelowText={shouldRenderOverageTagsBelowText}
          />
        )}

        {remaining && (
          <Space direction='vertical' size='xsmall'>
            <Space.Divider />

            <BudgetCategoryCardSubtotal
              label={intl.formatMessage(messages.subtotalsRemainingLabel)}
              amount={remaining.amount}
              percentage={remaining?.percentage?.amount}
              percentageOverBudget={remaining?.percentage?.overage}
              isTotal={true}
              shouldRenderPercentageFirst={shouldRenderPercentagesFirst}
              shouldRenderOverageTagBelowText={shouldRenderOverageTagsBelowText}
            />
          </Space>
        )}

        <Spacer />

        {pendingRequests && (
          <>
            <BudgetCategoryCardSubtotal
              label={intl.formatMessage(messages.subtotalsPendingLabel)}
              amount={pendingRequests.amount}
              percentage={pendingRequests?.percentage?.amount}
              percentageOverBudget={remaining?.percentage?.overage}
              indicatorColor='yellow'
              shouldRenderPercentageFirst={shouldRenderPercentagesFirst}
              shouldRenderOverageTagBelowText={shouldRenderOverageTagsBelowText}
            />
            <Space.Divider />
          </>
        )}

        {remainingAfterPendingRequests ? (
          <BudgetCategoryCardSubtotal
            label={intl.formatMessage(
              messages.subtotalsRemainingAfterRequestsLabel
            )}
            amount={remainingAfterPendingRequests.amount}
            percentage={remainingAfterPendingRequests?.percentage?.amount}
            percentageOverBudget={
              remainingAfterPendingRequests?.percentage?.overage
            }
            isTotal={true}
            indicatorColor='lightGrey'
            shouldRenderPercentageFirst={shouldRenderPercentagesFirst}
            shouldRenderOverageTagBelowText={shouldRenderOverageTagsBelowText}
          />
        ) : (
          <BudgetCategoryCardSubtotal
            label={intl.formatMessage(
              messages.subtotalsTotalAfterRequestsLabel
            )}
            amount={totalSpend.amount}
            percentage={totalSpend?.percentage?.amount}
            percentageOverBudget={totalSpend?.percentage?.overage}
            isTotal={true}
            shouldRenderPercentageFirst={shouldRenderPercentagesFirst}
          />
        )}
      </Space>
    </BudgetCategoryCardSubtotalsStyled>
  )
}
